"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Timeseries = exports.TimeseriesInitialisationError = exports.WindowError = exports.Series = void 0;
const date_fns_1 = require("date-fns");
const types_1 = require("./types");
const binary_1 = require("./utils/binary");
const search_1 = __importDefault(require("./utils/search"));
const aggregate_1 = require("./utils/aggregate");
const object_1 = require("./utils/object");
class Series {
    constructor(id, dataType, data, length, offset = 0, attributes) {
        this.id = id;
        this.dataType = dataType;
        this.offset = offset;
        this.attributes = attributes;
        if (data !== undefined) {
            // Create DataView from data
            if ((0, object_1.isDataView)(data)) {
                this.dataView = data;
            }
            else if ((0, object_1.isArrayBuffer)(data)) {
                this.dataView = new DataView(data);
            }
            else if (Array.isArray(data)) {
                this.dataView = new DataView(new ArrayBuffer(data.length * this.dataType.bytes));
                this.length = this.offset + this.dataViewLength;
                this.fromArray(data, this.offset);
            }
            else {
                this.dataView = new DataView(new ArrayBuffer(data * this.dataType.bytes));
                for (let i = 0; i < data; i++) {
                    (0, binary_1.encodeReading)(this.dataView, this.dataType, i, null);
                }
            }
            if (attributes) {
                this.attributes = attributes;
            }
        }
        // Length can be longer than the dataView and offset
        this.length = length !== null && length !== void 0 ? length : this.offset + this.dataViewLength;
    }
    get dataViewLength() {
        if (this.dataView === undefined) {
            return 0;
        }
        return this.dataView.byteLength / this.dataType.bytes;
    }
    extend(length, offset) {
        // Return extended series with nulls
        if (length - offset < this.dataViewLength) {
            throw new Error("Trying to shrink series");
        }
        // console.log(this.attributes)
        return new Series(this.id, this.dataType, this.dataView, length, offset, this.attributes);
    }
    asArray() {
        if (this.dataView === undefined) {
            return [];
        }
        let result = [];
        for (let i = 0; i < this.length; i++) {
            result.push(this.get(i));
        }
        return result;
    }
    fromArray(data, offset = 0) {
        if (this.dataView === undefined) {
            throw RangeError("DataView is undefined");
        }
        if (this.length < data.length + offset) {
            throw RangeError("Data array too large");
        }
        this.offset = offset;
        for (let j = 0; j < data.length; j++) {
            (0, binary_1.encodeReading)(this.dataView, this.dataType, j, data[j]);
        }
    }
    set(index, value) {
        if (this.dataView === undefined) {
            throw RangeError("DataView is undefined");
        }
        (0, binary_1.encodeReading)(this.dataView, this.dataType, index - this.offset, value);
    }
    get(index) {
        if (this.dataView === undefined) {
            throw RangeError("Series is empty");
        }
        index -= this.offset;
        if (index < 0) {
            return null;
        }
        if (index >= this.dataViewLength) {
            return null;
        }
        return (0, binary_1.decodeReading)(this.dataView, this.dataType, index);
    }
    slice(begin, end) {
        if (this.dataView === undefined) {
            throw RangeError("Series is empty");
        }
        begin = Math.max(begin - this.offset, 0);
        let offset = Math.max(this.offset - begin, 0);
        end = Math.min(end - this.offset, this.dataViewLength);
        let length = end - begin;
        return new Series(this.id, this.dataType, this.dataView.buffer.slice(begin * this.dataType.bytes, (end + 1) * this.dataType.bytes), length, offset, this.attributes);
    }
    nzmin() {
        return (0, aggregate_1.nzmin)(this.asArray());
    }
    min() {
        return (0, aggregate_1.min)(this.asArray());
    }
    max() {
        return (0, aggregate_1.max)(this.asArray());
    }
    sum() {
        return (0, aggregate_1.sum)(this.asArray());
    }
    mean() {
        return (0, aggregate_1.mean)(this.asArray());
    }
    median() {
        return (0, aggregate_1.median)(this.asArray());
    }
    stddev() {
        return (0, aggregate_1.standardDeviation)(this.asArray());
    }
    var() {
        return (0, aggregate_1.variance)(this.asArray());
    }
}
exports.Series = Series;
class WindowError extends Error {
    constructor(msg) {
        super(msg);
        Object.setPrototypeOf(this, WindowError.prototype);
    }
}
exports.WindowError = WindowError;
class TimeseriesInitialisationError extends Error {
    constructor(msg) {
        super(msg);
        Object.setPrototypeOf(this, TimeseriesInitialisationError.prototype);
    }
}
exports.TimeseriesInitialisationError = TimeseriesInitialisationError;
function findFirstLastTimeAndInterval(timeseries) {
    const interval = timeseries[0].interval;
    if (interval === null) {
        throw new TimeseriesInitialisationError("Timeseries with unfixed intervals are not supported");
    }
    let earliest = Infinity;
    let latest = 0;
    for (const ts of timeseries) {
        if (ts.interval !== interval) {
            throw new TimeseriesInitialisationError("Timeseries have mismatching intervals");
        }
        if ((ts.firstReadingTime - earliest) % interval) {
            throw new TimeseriesInitialisationError("Intervals are not aligned");
        }
        earliest = Math.min(earliest, ts.firstReadingTime);
        latest = Math.max(latest, ts.lastReadingTime);
    }
    return [earliest, latest, interval];
}
class Timeseries {
    constructor(timestamps, data, _interval = null) {
        this._interval = _interval;
        this.columns = new Map();
        this.timestamps = [];
        this._init(timestamps, data);
    }
    static combine(...timeseries) {
        const [firstTime, lastTime, interval] = findFirstLastTimeAndInterval(timeseries);
        const timestamps = [];
        let timestamp = firstTime;
        while (timestamp <= lastTime) {
            timestamps.push(timestamp);
            timestamp += interval;
        }
        const records = timeseries.map((member) => {
            return member
                .series()
                .extend(timestamps.length, (member.firstReadingTime - firstTime) / interval);
        });
        return new Timeseries(timestamps, records, interval);
    }
    get interval() {
        return this._interval;
    }
    _init(timestamps, data) {
        this.timestamps = timestamps;
        for (const d of data) {
            this.columns.set(d.id, d);
        }
        if (this.columns.size !== data.length) {
            console.warn("Duplicate series id");
        }
    }
    get firstReadingTime() {
        if (this.count == 0) {
            return 0;
        }
        return this.timestamps[0];
    }
    get lastReadingTime() {
        if (this.count == 0) {
            return 0;
        }
        return this.timestamps[this.count - 1];
    }
    get count() {
        return this.timestamps.length;
    }
    series(id) {
        if (id !== undefined) {
            const series = this.columns.get(id);
            if (series == null) {
                throw new Error("Series not found");
            }
            return series;
        }
        else if (this.isGroup) {
            throw new Error("Series id must be specified");
        }
        else {
            return this.columns.values().next().value;
        }
    }
    points(id) {
        id = id !== null && id !== void 0 ? id : this.columns.keys().next().value;
        if (id === undefined) {
            throw new Error("No columns");
        }
        const column = this.columns.get(id);
        if (column === undefined) {
            throw new Error("Column not found");
        }
        const result = [];
        for (let i = 0; i < this.count; i++) {
            result.push([this.timestamps[i], column.get(i)]);
        }
        return result;
    }
    get isGroup() {
        return this.columns.size > 1;
    }
    getRowAtIndex(i, options) {
        let row = [];
        for (let [id, column] of this.columns.entries()) {
            row.push([id, column.get(i)]);
        }
        if ((options === null || options === void 0 ? void 0 : options.sortBy) && this.isGroup) {
            if (options.sortBy === "asc") {
                row.sort((a, b) => { var _a, _b; return ((_a = a[1]) !== null && _a !== void 0 ? _a : 0) - ((_b = b[1]) !== null && _b !== void 0 ? _b : 0); });
            }
            else if (options.sortBy === "desc") {
                row.sort((a, b) => { var _a, _b; return ((_a = b[1]) !== null && _a !== void 0 ? _a : 0) - ((_b = a[1]) !== null && _b !== void 0 ? _b : 0); });
            }
        }
        return row;
    }
    getRow(timestamp, options) {
        const index = this.indexOf(timestamp);
        if (index === null)
            return [];
        return this.getRowAtIndex(index, options);
    }
    indexOf(timestamp) {
        const index = (0, search_1.default)(this.timestamps, timestamp, "exact");
        if (index < 0)
            return null;
        return index;
    }
    window(options = {}) {
        if (this.count === 0) {
            // Nothing has changed
            return this;
        }
        let { from = this.firstReadingTime, to = this.lastReadingTime, clip = false, } = options;
        if (from > to) {
            throw new WindowError("Start cannot be later than end");
        }
        if (from < this.firstReadingTime) {
            if (!clip)
                throw new WindowError("Start is before firstReadingTime");
            from = this.firstReadingTime;
        }
        if (to > this.lastReadingTime) {
            if (!clip)
                throw new WindowError("End is after lastReadingTime");
            to = this.lastReadingTime;
        }
        if (from === to) {
            // Window contains no time, so return an empty timeseries
            return new Timeseries([], Array.from(this.columns, ([id, column]) => new Series(id, types_1.DataType.fromKey(2 /* DataTypeKey.UInt16 */))));
        }
        // Start element is the first reading >= from
        // End element is the last reading <= to
        const startElement = (0, search_1.default)(this.timestamps, from, "ceil");
        const endElement = (0, search_1.default)(this.timestamps, to, "floor") + 1;
        return new Timeseries(this.timestamps.slice(startElement, endElement), Array.from(this.columns, ([id, column]) => {
            return column.slice(startElement, endElement);
        }), this.interval);
    }
    rollUp({ duration, aggregate = types_1.Aggregate.SUM, from = this.firstReadingTime, to = this.lastReadingTime,
    // utc = false
     }) {
        const resultDataType = types_1.DataType.fromKey(9 /* DataTypeKey.Float64 */);
        const timestamps = [];
        const columns = Array.from(this.columns, ([id, column]) => column);
        const result = columns.map((column) => []);
        let durationStart = from;
        while (durationStart < to) {
            let durationEnd = (0, date_fns_1.add)(durationStart, duration).valueOf() - 1000;
            let durationStartDisplay = durationStart;
            let durationEndDisplay = durationEnd;
            // if (utc) {
            //   // Get timezone offsets
            //   const startOffset = new Date(durationStart).getTimezoneOffset();
            //   const endOffset = new Date(durationEnd).getTimezoneOffset();
            //   durationStartDisplay += startOffset < 0 ? 3600000 : 0;
            //   durationEndDisplay += endOffset < 0 ? 1800000 : -1800000;
            // }
            // console.log('durationStartDisplay', new Date(durationStartDisplay).toISOString(), 'durationEndDisplay', new Date(durationEndDisplay).toISOString())
            const window = this.window({
                from: durationStartDisplay,
                to: durationEndDisplay,
                clip: true,
            });
            // console.log('window.count', window.count)
            timestamps.push(durationStartDisplay);
            for (const [seriesIndex, series] of columns.entries()) {
                result[seriesIndex].push(aggregate(window.series(series.id).asArray()));
            }
            durationStart = durationEnd + 1000;
        }
        return new Timeseries(timestamps, result.map((data, i) => {
            const series = new Series(columns[i].id, resultDataType, data, data.length, 0, columns[i].attributes);
            return series;
        }, false));
    }
    _aggregateColumns(ids, aggregateFunction) {
        // Filter by ids
        const columns = [];
        for (const [id, series] of this.columns.entries()) {
            if (ids.includes(id)) {
                columns.push(series);
            }
        }
        const result = [];
        for (let i = 0; i < this.count; i++) {
            result.push(aggregateFunction(columns.map((col) => col.get(i))));
        }
        return result;
    }
    rollIn({ aggregate = types_1.Aggregate.SUM, id = "data", } = {}) {
        const resultDataType = types_1.DataType.fromKey(9 /* DataTypeKey.Float64 */);
        return new Timeseries(this.timestamps, [
            new Series(id, resultDataType, this._aggregateColumns([...this.columns.keys()], aggregate)),
        ], this.interval);
    }
    apply({ baseline, func }) {
        const resultDataType = types_1.DataType.fromKey(9 /* DataTypeKey.Float64 */);
        const all = Timeseries.combine(baseline, this);
        const columns = [...this.columns.keys()];
        return new Timeseries(all.timestamps, columns.map((id) => {
            return new Series(id, resultDataType, all._aggregateColumns([baseline.series().id, id], (values) => func(values[0], values[1])));
        }), all.interval);
    }
    nzmin() {
        return this.series().nzmin();
    }
    min() {
        return this.series().min();
    }
    max() {
        return this.series().max();
    }
    sum() {
        return this.series().sum();
    }
    mean() {
        return this.series().mean();
    }
    median() {
        return this.series().median();
    }
    stddev() {
        return this.series().stddev();
    }
    var() {
        return this.series().var();
    }
}
exports.Timeseries = Timeseries;
//# sourceMappingURL=timeseries.js.map